import type { LocationPublic } from '@repo/types/locations';
import type { MapCameraChangedEvent, MapCameraProps } from '@vis.gl/react-google-maps';

import { cn } from '@repo/lib/cn';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import { CompassIcon, Link2, MapIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { Button, ButtonGroup } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { useTheme } from '@/context/theme';

/**
 * Props for LocationCard component.
 * @property {LocationPublic} location - Location data to display.
 * @property {string} [className] - Optional additional class names.
 * @property {boolean} [staticMap] - If true, disables map interaction and shows "Open Map" button.
 */
/**
 * Only the fields this card actually reads. Narrower than `LocationPublic` so
 * trimmed payloads render too — the parent calendar returns a location without
 * its address, which the markup already hides when empty.
 */
export type LocationCardLocation = Pick<LocationPublic, 'id' | 'name' | 'coordinates'> & Partial<Pick<LocationPublic, 'address'>>;

interface LocationCardProps {
  location: LocationCardLocation;
  className?: string;
  staticMap?: boolean;
}

export default function LocationCard(locationCardProps: LocationCardProps) {
  const { location, className, staticMap } = locationCardProps;
  const { coordinates, name } = location;
  const theme = useTheme();

  const { lat, lng } = useMemo(() => {
    const [_lat, _lng] = coordinates.split(',');
    return { lat: Number(_lat), lng: Number(_lng) };
  }, [coordinates]);

  const [cameraProps, setCameraProps] = useState<MapCameraProps>({
    zoom: 15,
    center: { lat, lng },
  });
  const handleCameraChange = useCallback(
    (ev: MapCameraChangedEvent) => {
      if (staticMap) return;
      setCameraProps(ev.detail);
    },
    [staticMap]
  );

  const handleGoBackToPoint = () => {
    if (staticMap) {
      return;
    }
    setCameraProps({
      zoom: 15,
      center: { lat, lng },
    });
  };

  return (
    <div className={cn('border-border-primary bg-card relative h-full w-full overflow-hidden rounded-2xl border', className)}>
      <APIProvider apiKey={import.meta.env.VITE_GOOGLE_API_KEY} language={'pl'}>
        <Map
          mapId={'49ae42fed52588c3'}
          defaultCenter={{ lat, lng }}
          defaultZoom={15}
          colorScheme={theme.googleMapsTheme}
          gestureHandling={'cooperative'}
          disableDefaultUI={true}
          clickableIcons={false}
          {...cameraProps}
          onCameraChanged={handleCameraChange}
          style={{ height: 'calc(100% - var(--spacing)*14)' }}
        >
          <Marker position={{ lat, lng }} />
        </Map>
      </APIProvider>
      <div className={'border-border-primary flex h-14 w-full items-center justify-between border-t p-2 md:p-3'}>
        <div className="flex items-center gap-3 overflow-hidden">
          <MapIcon className="text-accent-foreground size-4 shrink-0" />
          <div className="flex flex-col gap-0.5 overflow-hidden">
            <p className="text-accent-foreground text-sm">{name}</p>
            <p className="text-muted-foreground truncate text-xs empty:hidden">{location.address}</p>
          </div>
        </div>
        {staticMap ? (
          <Button variant={'secondary'} size={'sm'} asChild className="absolute top-2 right-2 z-10 h-7 min-h-7 text-xs">
            <Link to={`https://www.google.com/maps/place/${coordinates}`} target="_blank">
              Otwórz Mapę <Link2 className="size-3" />
            </Link>
          </Button>
        ) : (
          <>
            <ButtonGroup className="absolute top-2 right-2 z-10">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size={'sm'}>
                    <MapIcon />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className={'md:max-w-96'}>
                  <AlertDialogBody>
                    <AlertDialogHeader>
                      <FeaturedIcon>
                        <MapIcon />
                      </FeaturedIcon>
                      <div className={'flex min-w-0 grow flex-col gap-0.5 overflow-hidden'}>
                        <AlertDialogTitle>Chcesz przejść do strony Google Maps?</AlertDialogTitle>
                        <p className={'text-muted-foreground text-xs'}>Przejdź do strony google maps aby dokładnie sprawdzić lokalizację.</p>
                      </div>
                    </AlertDialogHeader>
                  </AlertDialogBody>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Anuluj</AlertDialogCancel>
                    <Link to={`https://www.google.com/maps/place/${coordinates}`} target={'_blank'}>
                      <AlertDialogAction>
                        <MapIcon /> Przejdź do map
                      </AlertDialogAction>
                    </Link>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button variant="outline" size={'sm'} disabled={!location} onClick={handleGoBackToPoint}>
                <CompassIcon />
              </Button>
            </ButtonGroup>
          </>
        )}
      </div>
    </div>
  );
}
